import React, { useState, useRef } from 'react';
import { FileText, Mail, ExternalLink, Sparkles, Code, Cpu, CheckCircle2, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import resumePdf from '../assets/JERWIN-GUBAT-CV.pdf';
import TextReveal from './animations/TextReveal';
import { MagneticButton } from './animations/MagneticButton';
import { IS_PRERENDER } from '../lib/prerender';
import { scrollToElement } from '../lib/scroll';

function Banner() {
    const [activeTab, setActiveTab] = useState('code');

    const openResume = () => {
        window.open(resumePdf, '_blank');
    };

    const openGithub = () => {
        window.open('https://github.com/jerwingubat', '_blank');
    };

    const openLinkedin = () => {
        window.open('https://www.linkedin.com/in/jerwin-gubat-883944216', '_blank');
    };

    const openMedium = () => {
        window.open('https://medium.com/@jerwinfaderanga', '_blank');
    };

    const scrollToProjects = () => {
        scrollToElement(document.getElementById('projects'), { offset: 80 });
    };

    const startDate = new Date("2021-02-02");

    const calculateYears = (startDate) => {
        const today = new Date();

        let years = today.getFullYear() - startDate.getFullYear();

        const hasHadAnniversary =
            today.getMonth() > startDate.getMonth() ||
            (today.getMonth() === startDate.getMonth() &&
                today.getDate() >= startDate.getDate());

        if (!hasHadAnniversary) {
            years--;
        }

        return years;
    };

    const yearsOfExperience = calculateYears(startDate);

    const heroRef = useRef(null);
    const prefersReducedMotion = useReducedMotion();

    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ['start start', 'end start']
    });

    const headingY = useTransform(scrollYProgress, [0, 1], [0, -90]);
    const bgY = useTransform(scrollYProgress, [0, 1], [0, 60]);
    const cardY = useTransform(scrollYProgress, [0, 1], [0, 60]);
    const cardScale = useTransform(scrollYProgress, [0, 1], [1, 0.95]);

    const tabs = [
        { id: 'code', label: 'Code', icon: <Code className="w-3 h-3 inline mr-1" /> },
        { id: 'metrics', label: 'Metrics', icon: <Cpu className="w-3 h-3 inline mr-1" /> }
    ];

    return (
        <section ref={heroRef} className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden min-h-[90vh] flex items-center">
            {/* Soft Radial Ambient Glow (moves slower than scroll) */}
            <motion.div
                style={prefersReducedMotion ? undefined : { y: bgY, willChange: 'transform' }}
                className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none -z-10"
            />
            <motion.div
                style={prefersReducedMotion ? undefined : { y: bgY, willChange: 'transform' }}
                className="absolute top-1/3 right-10 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none -z-10"
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">

                    {/* Left Column: Headline, Bio & CTAs (heading drifts up as the hero scrolls away) */}
                    <motion.div
                        className="lg:col-span-7 space-y-6 text-left"
                        style={prefersReducedMotion ? undefined : { y: headingY, willChange: 'transform' }}
                    >

                        {/* Availability Pill Badge */}
                        <motion.div
                            initial={IS_PRERENDER ? false : { opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 backdrop-blur-md text-emerald-400 text-xs font-medium tracking-wide"
                        >
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                            </span>
                            <span>Available for new projects & opportunities</span>
                        </motion.div>

                        {/* Title & Gradient Headline with staggered masked reveal */}
                        <div className="space-y-2">
                            <motion.span
                                initial={IS_PRERENDER ? false : { opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                className="block text-sm font-semibold uppercase tracking-widest text-indigo-400"
                            >
                                Jerwin Gubat • AI Engineer • Cavite, Philippines
                            </motion.span>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-sora font-extrabold text-white tracking-tight leading-[1.1]">
                                <TextReveal
                                    text="Freelance Fullstack Developer"
                                    delay={0.15}
                                    byChar
                                    blur={8}
                                    className="block"
                                />
                                <TextReveal
                                    text="Building Intelligent Web & AI Solutions"
                                    delay={0.4}
                                    stagger={0.05}
                                    byChar
                                    blur={6}
                                    className="block gradient-text"
                                />
                            </h1>
                        </div>

                        {/* Bio / Description */}
                        <motion.p
                            initial={IS_PRERENDER ? false : { opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.9, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            className="text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed font-normal"
                        >
                            I'm <strong className="text-white font-semibold">Jerwin Gubat</strong>, a freelance fullstack developer and programmer based in Cavite, Philippines — Founder of OptiByte Software Solutions and Computer Science Instructor at Cavite State University. I build scalable fullstack web applications, AI &amp; machine learning systems, computer vision software, and modern interactive user experiences.
                        </motion.p>

                        {/* CTAs with magnetic effect */}
                        <motion.div
                            initial={IS_PRERENDER ? false : { opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.05, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            className="flex flex-wrap items-center gap-4 pt-2"
                        >
                            <MagneticButton strength={0.25}>
                                <button
                                    onClick={scrollToProjects}
                                    className="px-6 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all duration-300 flex items-center gap-2 group"
                                >
                                    <span>Explore Projects</span>
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                            </MagneticButton>

                            <MagneticButton strength={0.2}>
                                <button
                                    onClick={openResume}
                                    className="px-6 py-3.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-indigo-500/40 text-slate-200 hover:text-white text-sm font-semibold backdrop-blur-md transition-all duration-300 flex items-center gap-2"
                                >
                                    <FileText className="w-4 h-4 text-indigo-400" />
                                    <span>Download Resume</span>
                                </button>
                            </MagneticButton>
                        </motion.div>

                        {/* Social Links & Quick Stats */}
                        <motion.div
                            initial={IS_PRERENDER ? false : { opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1.2, duration: 0.8 }}
                            className="pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-4"
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-xs text-slate-400 font-medium">Connect:</span>
                                <button
                                    onClick={openGithub}
                                    className="p-2.5 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-indigo-500/40 hover:bg-indigo-500/10 transition-all flex items-center justify-center"
                                    title="GitHub"
                                >
                                    <i className="fa-brands fa-github text-base"></i>
                                </button>
                                <button
                                    onClick={openLinkedin}
                                    className="p-2.5 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-indigo-500/40 hover:bg-indigo-500/10 transition-all flex items-center justify-center"
                                    title="LinkedIn"
                                >
                                    <i className="fa-brands fa-linkedin text-base"></i>
                                </button>
                                <button
                                    onClick={openMedium}
                                    className="p-2.5 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-indigo-500/40 hover:bg-indigo-500/10 transition-all flex items-center justify-center"
                                    title="Medium Articles"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                </button>
                                <a
                                    href="mailto:jerwingubat@gmail.com"
                                    className="p-2.5 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-indigo-500/40 hover:bg-indigo-500/10 transition-all flex items-center justify-center"
                                    title="Send Email"
                                >
                                    <Mail className="w-4 h-4" />
                                </a>
                            </div>

                            <div className="flex items-center gap-6 text-xs text-slate-400 font-medium">
                                <div>
                                    <span className="block text-white font-bold text-base font-sora">
                                        {yearsOfExperience}+ Years
                                    </span>
                                    <span>Dev Experience</span>
                                </div>

                                <div className="h-8 w-[1px] bg-white/10" />
                                <div>
                                    <span className="block text-white font-bold text-base font-sora">10+ Projects</span>
                                    <span>Built & Shipped</span>
                                </div>
                            </div>
                        </motion.div>

                    </motion.div>

                    {/* Right Column: Floating Visual Card / Interactive Visual Code (card recedes slightly on scroll) */}
                    <motion.div
                        className="lg:col-span-5 relative"
                        style={prefersReducedMotion ? undefined : { y: cardY, scale: cardScale, willChange: 'transform' }}
                    >
                    <motion.div
                        initial={IS_PRERENDER ? false : { opacity: 0, y: 30, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 0.7, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-5 relative"
                    >
                        {/* Glow effect behind card */}
                        <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-cyan-500 rounded-2xl blur-lg opacity-30 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse-glow" />

                        <div className="relative rounded-2xl bg-[#0F172A]/90 border border-white/10 backdrop-blur-xl shadow-2xl p-5 sm:p-6 overflow-hidden">

                            {/* Card Header Bar */}
                            <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-rose-500/80" />
                                    <div className="w-3 h-3 rounded-full bg-amber-500/80" />
                                    <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
                                    <span className="text-xs text-slate-400 font-mono ml-2">developer_profile.ts</span>
                                </div>
                                <div className="relative flex items-center gap-1 bg-white/5 rounded-lg p-1 border border-white/10">
                                    {tabs.map((tab) => {
                                        const isActive = activeTab === tab.id;
                                        return (
                                            <button
                                                key={tab.id}
                                                onClick={() => setActiveTab(tab.id)}
                                                className="relative px-2.5 py-1 text-xs font-mono rounded flex items-center"
                                            >
                                                {isActive && (
                                                    <motion.span
                                                        layoutId="banner-active-tab"
                                                        className="absolute inset-0 bg-indigo-600 rounded"
                                                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                                                    />
                                                )}
                                                <span className={`relative z-10 ${isActive ? 'text-white' : 'text-slate-400 hover:text-slate-200'}`}>
                                                    {tab.icon}
                                                    {tab.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Card Content Body */}
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeTab}
                                    initial={IS_PRERENDER ? false : { opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {activeTab === 'code' ? (
                                    <div className="font-mono text-xs text-slate-300 space-y-2 leading-relaxed bg-[#090D16] p-4 rounded-xl border border-white/5 overflow-x-auto text-left">
                                        <div>
                                            <span className="text-indigo-400">const</span> <span className="text-cyan-300">engineer</span> = &#123;
                                        </div>
                                        <div className="pl-4">
                                            <span className="text-slate-400">name:</span> <span className="text-emerald-400">'Jerwin Gubat'</span>,
                                        </div>
                                        <div className="pl-4">
                                            <span className="text-slate-400">roles:</span> [
                                            <span className="text-amber-300">'Fullstack Dev'</span>, <span className="text-amber-300">'CS Lecturer'</span>
                                            ],
                                        </div>
                                        <div className="pl-4">
                                            <span className="text-slate-400">techStack:</span> [
                                            <span className="text-cyan-400">'React'</span>, <span className="text-cyan-400">'Node.js'</span>, <span className="text-cyan-400">'Python'</span>, <span className="text-cyan-400">'MTCNN'</span>
                                            ],
                                        </div>
                                        <div className="pl-4">
                                            <span className="text-slate-400">location:</span> <span className="text-emerald-400">'Cavite, Philippines'</span>,
                                        </div>
                                        <div className="pl-4">
                                            <span className="text-slate-400">status:</span> <span className="text-emerald-400">'AI Solutions/Building Innovation'</span>
                                        </div>
                                        <div>&#125;;</div>
                                        <div className="pt-2 text-indigo-400">
                                            <span className="text-indigo-400">await</span> <span className="text-cyan-300">engineer</span>.<span className="text-amber-300">buildSolutions</span>();
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-3 bg-[#090D16] p-4 rounded-xl border border-white/5 text-left">
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-slate-400 flex items-center gap-1.5">
                                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Web & Mobile Apps
                                            </span>
                                            <span className="text-emerald-400 font-bold">100% Client Satisfaction</span>
                                        </div>
                                        <div className="w-full bg-white/10 rounded-full h-1.5">
                                            <div className="bg-gradient-to-r from-indigo-500 to-emerald-400 h-1.5 rounded-full w-[95%]" />
                                        </div>

                                        <div className="flex items-center justify-between text-xs pt-2">
                                            <span className="text-slate-400 flex items-center gap-1.5">
                                                <CheckCircle2 className="w-3.5 h-3.5 text-indigo-400" /> AI/ML Face Recognition
                                            </span>
                                            <span className="text-indigo-400 font-bold">MTCNN + ResNet</span>
                                        </div>
                                        <div className="w-full bg-white/10 rounded-full h-1.5">
                                            <div className="bg-gradient-to-r from-indigo-500 to-cyan-400 h-1.5 rounded-full w-[90%]" />
                                        </div>

                                        <div className="flex items-center justify-between text-xs pt-2">
                                            <span className="text-slate-400 flex items-center gap-1.5">
                                                <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400" /> CS Curriculum & Teaching
                                            </span>
                                            <span className="text-cyan-400 font-bold">University Level</span>
                                        </div>
                                        <div className="w-full bg-white/10 rounded-full h-1.5">
                                            <div className="bg-gradient-to-r from-cyan-500 to-indigo-500 h-1.5 rounded-full w-[100%]" />
                                        </div>
                                    </div>
                                )}
                                </motion.div>
                            </AnimatePresence>

                            {/* Card Footer badges */}
                            <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-[11px] text-slate-400">
                                <span className="flex items-center gap-1.5">
                                    <Sparkles className="w-3.5 h-3.5 text-indigo-400" /> Modern Stack Enabled
                                </span>
                                <span className="font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                                    System Active
                                </span>
                            </div>

                        </div>
                    </motion.div>
                    </motion.div>

                </div>
            </div>
        </section>
    );
}

export default Banner;